import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, apiSubmit, PublicBidView, BidFormPayload, ApiError, fieldErrorsFrom, WorkItem } from '../lib/api';
import { BidForm } from '../components/BidForm';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Motif } from '../components/ui/Motif';
import { fireConfetti } from '../components/ui/confetti';

export default function PublicBidPage() {
  const { token } = useParams<{ token: string }>();
  const [done, setDone] = useState<number | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['public-bid', token],
    queryFn: () => api<PublicBidView>(`/p/bid/${token}`),
    enabled: !!token,
    retry: false,
  });

  const submit = useMutation({
    mutationFn: (p: BidFormPayload) =>
      apiSubmit<{ ok: boolean; round_number: number }>(`/p/bid/${token}/submit`, p),
    onSuccess: (r) => { setDone(r.round_number); void fireConfetti('submit'); },
    onError: (err: ApiError) => {
      setFieldErrors(fieldErrorsFrom(err));
    },
  });

  const shell = (body: React.ReactNode) => (
    <div className="portal" style={{ maxWidth: 880, margin: '0 auto', padding: '24px 16px' }}>{body}</div>
  );

  if (isLoading) return shell(<div className="card"><Skeleton lines={4} /></div>);
  if (isError || !data)
    return shell(
      <EmptyState
        motif="sorry"
        title="这条链接已经失效了 / This link has expired"
        hint="请让 WTL 重新发一条给您。 / Ask WTL to send you a fresh one."
      />,
    );

  if (data.closed)
    return shell(
      <>
        <h2>{data.package.name}</h2>
        <EmptyState
          motif="sorry"
          title={data.past_due ? '哎呀，截标时间过了 / The deadline has passed' : '投标已结束 / Bidding is closed'}
          hint={data.past_due
            ? `${data.due ? `截止于 ${new Date(data.due).toLocaleString('zh-CN')}。` : ''}有特殊情况？直接联系 WTL 项目组说明。 / Something came up? Reach out to the WTL project team.`
            : '本包的投标已经收尾，感谢您的参与。 / This package has wrapped up — thank you for taking part.'}
        />
      </>,
    );

  if (done != null)
    return shell(
      <>
        <h2>{data.package.name}</h2>
        <div className="card enter-pop" style={{ padding: 16, textAlign: 'center' }}>
          <Motif variant="cheer" size={56} className="motif-center" />
          <div style={{ fontSize: 18, marginTop: 6 }}>
            🎉 报价交到了！第 {done} 轮已送达 WTL。 / Bid received — round {done} is with WTL.
          </div>
          <div className="muted small" style={{ marginTop: 4 }}>接下来交给 WTL 审阅，有回音会第一时间通知您。 / WTL will review it and get back to you.</div>
          <button
            style={{ marginTop: 12 }}
            onClick={() => {
              setDone(null);
              refetch();
            }}
          >
            提交修订（下一轮）/ Submit a revision (next round)
          </button>
        </div>
      </>,
    );

  const submitError = submit.isError
    ? ((submit.error as ApiError).body?.errors?.[0]?.message ?? '提交失败 / submit failed')
    : null;

  return shell(
    <>
      <h2 style={{ marginBottom: 2 }}>{data.package.name}</h2>
      <div className="muted small" style={{ marginBottom: 14 }}>
        {data.package.project_name} · 投标方 / Bidder:{' '}
        <strong>{data.engagement.labor_co_name}</strong>
        {data.next_round != null && (
          <> · 第 {data.next_round} 轮 / Round {data.next_round}</>
        )}
      </div>
      {data.due && (
        <div className="card small" style={{ padding: '8px 12px', marginBottom: 12, borderLeft: '3px solid var(--bad)', borderRadius: 0, background: 'var(--warn-soft)' }}>
          ⏰ 截标时间 / Deadline: <strong>{new Date(data.due).toLocaleString('zh-CN')}</strong> — 截止后将无法提交在线报价 / online submission closes at the deadline.
        </div>
      )}
      <BidForm
        key={data.next_round ?? 1}
        workItems={data.work_items as WorkItem[]}
        busy={submit.isPending}
        submitLabel={`提交报价 / Submit bid (Round ${data.next_round ?? 1})`}
        serverError={submitError}
        fieldErrors={fieldErrors}
        prefill={data.last_round}
        roundNumber={data.next_round}
        onSubmit={(p) => {
          setFieldErrors({});
          submit.mutate(p);
        }}
      />
    </>,
  );
}
